import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchIconAction } from '../../reducers/iconReducer'
import { fetchCollections, collectionSelector, collectionStatusSelector } from '../../reducers/collectionReducer'
import { AppDispatch } from '../../store'
import CollectionLayout from './CollectionLayout'
import MUITable from '../../modules/common/MUITable'
import TableLoading from '../../../_metronic/layout/components/Common/TableSkeletonLoading'
import { useCollectionTableConfig } from './components/CollectionTable'
import { KTSVG } from '../../../_metronic/helpers'

const CollectionsList = () => {
  const collectionData = useSelector(collectionSelector)
  const status = useSelector(collectionStatusSelector)
  const dispatch = useDispatch<AppDispatch>()

  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [sort, setSort] = useState(0);
  const [admin, setAdmin] = useState(false);
  const [notAdmin, setNotAdmin] = useState(false);
  const [both, setBoth] = useState(true);
  const [isActive, setIsActive] = useState<boolean | null>(null);

  const { getTableData, Modals } = useCollectionTableConfig();

  useEffect(() => {
    dispatch(fetchCollections())
    dispatch(fetchIconAction())
  }, [dispatch]);

  const toggleSort = () => {
    setSort((prevSort) => (prevSort + 1) % 3)
  }

  const sortData = (data: any[], sortType: string) => {
    return data.sort((a, b) => a?.name.localeCompare(b?.name) * (sortType === 'asc' ? 1 : -1))
  }

  function checkFilter() {
    let filteredData = [...collectionData]

    if (admin && isActive === true) {
      return filteredData.filter((f) => f.isAdmin === 1 && f.isActive === 1)
    } else if (admin && isActive === false) {
      return filteredData.filter((f) => f.isAdmin === 1 && f.isActive === 0)
    } else if (admin) {
      return filteredData.filter((f) => f.isAdmin === 1)
    } else if (notAdmin && isActive === true) {
      return filteredData.filter((f) => f.isAdmin !== 1 && f.isActive === 1)
    } else if (notAdmin && isActive === false) {
      return filteredData.filter((f) => f.isAdmin !== 1 && f.isActive === 0)
    } else if (notAdmin) {
      return filteredData.filter((f) => f.isAdmin !== 1)
    } else if (both && isActive === true) {
      return filteredData.filter((f) => f.isActive === 1)
    } else if (both && isActive === false) {
      return filteredData.filter((f) => f.isActive === 0)
    } else if (both) {
      return filteredData
    } else {
      return filteredData
    }
  }

  const SortedCollectionData = useMemo(() => {
    let data = checkFilter()
    
    switch (sort) {
      case 1:
        data = sortData([...data], 'asc')
        break
      case 2:
        data = sortData([...data], 'desc')
        break
      default:
        data = data
    }
    return data
  }, [sort, collectionData, admin, notAdmin, both, isActive])

  const tableData = getTableData(SortedCollectionData, currentPage, entriesPerPage);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newPerPage: number) => {
    setEntriesPerPage(newPerPage);
    setCurrentPage(1);
  };

  return (
    <CollectionLayout>
      <div className='d-flex justify-content-end mb-2 dropdown'>
        <div
          onClick={toggleSort}
          className={`btn btn-sm btn-icon btn-color-white border-0 me-3 ${sort ? 'bg-primary' : 'bg-secondary'
            }`}
        >
          <img
            style={{ filter: 'brightness(200 %) saturate(0%)' }}
            src={`${sort == 2 ? '/media/ztoa.png' : '/media/atoz.png'}`}
            alt='icon'
            className='my-1'
          />
        </div>
        <div
          style={{ cursor: 'auto' }}
          className='btn btn-sm btn-icon btn-color-white border-0 me-3 bg-primary'
        >
          {SortedCollectionData?.length}
        </div>

        <div>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-color-white btn-active-color-white border-0 me-n3 bg-primary'
            id='dropdownMenuButton2'
            data-bs-toggle='dropdown'
            aria-expanded='false'
          >
            <KTSVG path='/media/icons/duotune/general/gen024.svg' className='svg-icon-2' />
          </button>
          <ul
            className='dropdown-menu dropdown-menu-light w-250px w-md-300px'
            aria-labelledby='dropdownMenuButton2'
          >
            <div className='px-7 py-5'>
              <div className='fs-5 text-dark fw-bolder'>Filter Options</div>
            </div>

            <div className='separator border-gray-200'></div>

            <div className='px-7 pt-5'>
              <div className='mb-10'>
                <label className='form-label fw-bold mb-4'>Collection created by:</label>

                <div className='d-flex'>
                  <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                    <input
                      className='form-check-input'
                      type='radio'
                      onChange={(e) => {
                        setNotAdmin(true)
                        setAdmin(false)
                        setBoth(false)
                      }}
                      checked={notAdmin ? true : false}
                    />
                    <span className='form-check-label'>User</span>
                  </label>

                  <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                    <input
                      className='form-check-input'
                      type='radio'
                      onChange={() => {
                        setAdmin(true)
                        setNotAdmin(false)
                        setBoth(false)
                      }}
                      checked={admin ? true : false}
                    />
                    <span className='form-check-label'>Admin</span>
                  </label>
                  <label className='form-check form-check-sm form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='radio'
                      onChange={() => {
                        setBoth(true)
                        setAdmin(false)
                        setNotAdmin(false)
                      }}
                      checked={both ? true : false}
                    />
                    <span className='form-check-label'>Both</span>
                  </label>
                </div>
                <label className='form-label fw-bold mt-6'>Collection status:</label>

                <div className='d-flex'>
                  <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                    <input
                      className='form-check-input'
                      type='radio'
                      onChange={(e) => {
                        setIsActive(null)
                      }}
                      checked={isActive === null ? true : false}
                    />
                    <span className='form-check-label'>All</span>
                  </label>
                  <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                    <input
                      className='form-check-input'
                      type='radio'
                      onChange={(e) => {
                        setIsActive(true)
                      }}
                      checked={isActive === true ? true : false}
                    />
                    <span className='form-check-label'>Active</span>
                  </label>

                  <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                    <input
                      className='form-check-input'
                      type='radio'
                      onChange={(e) => {
                        setIsActive(false)
                      }}
                      checked={isActive === false ? true : false}
                    />
                    <span className='form-check-label'>InActive</span>
                  </label>
                </div>
              </div>
            </div>
          </ul>
        </div>
      </div>
      <div className='row mt-5'>
        {status === 'loading' ? (
          <TableLoading />
        ) : (
          <div className='card-body'>
            <MUITable
              data={tableData}
              page={currentPage}
              perPage={entriesPerPage}
              setPage={handlePageChange}
              setPerPage={handleRowsPerPageChange}
            />
          </div>
        )}
      </div>
      <Modals />
    </CollectionLayout>
  )
}

export default CollectionsList

